import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Route, Switch} from 'react-router';
import {Redirect} from 'react-router-dom';
import Loading from 'Shared/components/Loading';
import {Scope} from 'Shared/constants/Scope';
import useOnNavigateAway from 'Shared/utilities/useOnNavigateAway';
import IndependentAgentEntry from 'Users/components/AgentAdministration/AgencyAdministratorAgentEntry/IndependentAgentEntry';
import AgentManualEntry from 'Users/components/AgentAdministration/AgentManualEntry';
import FieldSalesLeaderEntry from 'Users/components/AgentAdministration/FieldSalesLeaderEntry';
import FieldVicePresidentEntry from 'Users/components/AgentAdministration/FieldVicePresidentEntry';
import VipEntry from 'Users/components/AgentAdministration/VipEntry';
import BOSAndBeneficiariesUserCreation from 'Users/components/BOSAndBeneficiariesUserCreation/BOSAndBeneficiariesUserCreation';
import SingleSignOnAccount from 'Users/components/SingleSignOn/SingleSignOnAccount';
import SingleSignOnAccounts from 'Users/components/SingleSignOn/SingleSignOnAccounts';
import EnrollTrackUserEntry from 'Users/components/UserAdministration/EnrollTrackUserEntry';
import UserAdministration from 'Users/components/UserAdministration/UserAdministration';
import UserProfile from 'Users/components/UserProfile/UserProfile';
import {clearUserAdministrationFilter} from 'Users/ducks/userAdministration';
import {selectUserScopesEither} from 'Users/selectors/userProfileSelectors';
import UserManualEntry from './components/UserAdministration/UserEntry/UserManualEntry';
import {UserType} from './models/BOSAndBeneficiariesUser';

const ROOT_URI = '/users';
export const UserRouteConstants = {
  ADD_AGENT: `${ROOT_URI}/agents/add`,
  ADD_ENROLL_TRACK_USER: `${ROOT_URI}/enrollTrack/add`,
  ADD_FIELD_SALES_LEADER: `${ROOT_URI}/FSLs/add`,
  ADD_BENEFICIARIES_USER: `${ROOT_URI}/beneficiaries/add`,
  ADD_BOSTOOLS_USER: `${ROOT_URI}/bostools/add`,
  ADD_FIELD_VICE_PRESIDENT: `${ROOT_URI}/FVPs/add`,
  ADD_SUPPORT_AGENT: `${ROOT_URI}/agents/add?isSupport=true`,
  ADD_VIP: `${ROOT_URI}/VIPs/add`,
  ADD_USER: `${ROOT_URI}/User/Add`,
  EDIT_USER: (userId: string) => `${ROOT_URI}/User/${userId}/edit`,
  EDIT_AGENT: (agentId: string) => `${ROOT_URI}/agents/edit/${agentId}`,
  EDIT_SUPPORT_AGENT: (agentId: string) => `${ROOT_URI}/agents/edit/${agentId}?isSupport=true`,
  EDIT_ENROLL_TRACK_USER: `${ROOT_URI}/enrollTrack/edit/`,
  EDIT_FIELD_SALES_LEADER_USER: (userId: string) => `${ROOT_URI}/FSLs/edit/${userId}`,
  EDIT_FIELD_VICE_PRESIDENT_USER: (userId: string) => `${ROOT_URI}/FVPs/edit/${userId}`,
  EDIT_VIP_USER: (userId: string) => `${ROOT_URI}/VIPs/edit/${userId}`,
  INDEPENDENT_AGENT_PREREGISTRATION: `${ROOT_URI}/agents/independent/preregistration`,
  EXTERNAL_USER_REGISTRATION: (userKey: string) => `${ROOT_URI}/external/registration?key=${userKey}`,
  EXTERNAL_USER_SIGN_UP: (iaKey: string) => `${ROOT_URI}/external/registration/${iaKey}`,
  INDEPENDENT_AGENT_SUPPORT: `${ROOT_URI}/agents/independent/preregistration?isSupport=true`,
  PASSWORD_RECOVERY: `${ROOT_URI}/recovery/password`,
  PASSWORD_RECOVERY_EMAIL_SENT: `${ROOT_URI}/recovery/password/emailSent`,
  PASSWORD_RESET_FORM: (requestKey: string) => `${ROOT_URI}/recovery/password/reset/${requestKey}`,
  PASSWORD_RESET_REQUEST_EXPIRED: `${ROOT_URI}/recovery/password/resetExpired`,
  PROFILE: `${ROOT_URI}/profile`,
  REGISTRATION: `${ROOT_URI}/registration`,
  SIGN_UP: `${ROOT_URI}/signup`,
  USERNAME_RECOVERY: `${ROOT_URI}/recovery/username`,
  USERS: ROOT_URI,
  SSO_ACCOUNTS: `${ROOT_URI}/ssoAccounts`,
  ADD_SSO_ACCOUNT: `${ROOT_URI}/ssoAccount/add`,
  EDIT_SSO_ACCOUNT: (username: string) => `${ROOT_URI}/ssoAccount/edit?username=${encodeURIComponent(username)}`,
};

const AddBOSToolsUserRoutes = () => {
  const currentScopesEither = useSelector(selectUserScopesEither);
  useBreadcrumbs('Add BOS Tools', UserRouteConstants.ADD_BOSTOOLS_USER);

  const renderAddBOSToolsUser = (currentScopes: Scope[]) => {
    const canAddBOSToolsUser = currentScopes.includes(Scope.AddBOSToolsUser);

    return canAddBOSToolsUser ? (
      <Route
        render={() => (
          <Switch>
            <Route
              exact
              path={UserRouteConstants.ADD_BOSTOOLS_USER}
              render={() => <BOSAndBeneficiariesUserCreation userType={UserType.BOSToolsUser} />}
            />
          </Switch>
        )}
      />
    ) : (
      <Redirect to={'/unauthorized'} />
    );
  };
  return (
    <Route
      path={ROOT_URI}
      render={() =>
        pipe(
          currentScopesEither,
          E.fold(() => <Loading />, renderAddBOSToolsUser)
        )
      }
    />
  );
};

const AddBeneficiariesUserRoutes = () => {
  const currentScopesEither = useSelector(selectUserScopesEither);
  useBreadcrumbs('Add Beneficiaries User', UserRouteConstants.ADD_BENEFICIARIES_USER);

  const renderAddBeneficiariesUser = (currentScopes: Scope[]) => {
    const canAddBeneficiariesUser = currentScopes.includes(Scope.AddBeneficiariesUser);

    return canAddBeneficiariesUser ? (
      <Route
        render={() => (
          <Switch>
            <Route
              exact
              path={UserRouteConstants.ADD_BENEFICIARIES_USER}
              render={() => <BOSAndBeneficiariesUserCreation userType={UserType.BeneficiariesUser} />}
            />
          </Switch>
        )}
      />
    ) : (
      <Redirect to={'/unauthorized'} />
    );
  };

  return (
    <Route
      path={ROOT_URI}
      render={() =>
        pipe(
          currentScopesEither,
          E.fold(() => <Loading />, renderAddBeneficiariesUser)
        )
      }
    />
  );
};

export default () => {
  const dispatch = useDispatch();
  useOnNavigateAway(UserRouteConstants.USERS, () => {
    dispatch(clearUserAdministrationFilter());
  });

  return (
    <Route
      path={`/users`}
      render={() => (
        <>
          <Route exact path={`${ROOT_URI}/ssoAccount/edit`} render={() => <SingleSignOnAccount />} />
          <Route exact path={UserRouteConstants.ADD_SSO_ACCOUNT} render={() => <SingleSignOnAccount />} />
          <Route exact path={UserRouteConstants.SSO_ACCOUNTS} render={() => <SingleSignOnAccounts />} />
          <Route exact path={UserRouteConstants.PROFILE} render={() => <UserProfile />} />
          <Route
            exact
            path={UserRouteConstants.SIGN_UP}
            render={routeProps => <IndependentAgentEntry {...routeProps} />}
          />
          <Route
            exact
            path={UserRouteConstants.ADD_AGENT}
            render={routeProps => <AgentManualEntry {...routeProps} />}
          />
          <Route
            exact
            path={UserRouteConstants.EDIT_AGENT(':agentId')}
            render={routeProps => <AgentManualEntry {...routeProps} />}
          />
          <Route
            exact
            path={UserRouteConstants.INDEPENDENT_AGENT_PREREGISTRATION}
            render={routeProps => <IndependentAgentEntry {...routeProps} />}
          />
          <Route exact path={UserRouteConstants.USERS} render={() => <UserAdministration />} />
          <Route exact path={UserRouteConstants.ADD_ENROLL_TRACK_USER} render={() => <EnrollTrackUserEntry />} />

          <Route exact path={UserRouteConstants.ADD_USER} render={() => <UserManualEntry />} />
          <Route exact path={UserRouteConstants.EDIT_USER(':userId')} render={() => <UserManualEntry />} />

          <Route exact path={UserRouteConstants.ADD_VIP} render={() => <VipEntry />} />
          <Route exact path={UserRouteConstants.EDIT_VIP_USER(':userId')} render={() => <VipEntry />} />
          <Route exact path={UserRouteConstants.ADD_FIELD_SALES_LEADER} render={() => <FieldSalesLeaderEntry />} />
          <Route
            exact
            path={UserRouteConstants.EDIT_FIELD_SALES_LEADER_USER(':userId')}
            render={() => <FieldSalesLeaderEntry />}
          />
          <Route exact path={UserRouteConstants.ADD_FIELD_VICE_PRESIDENT} render={() => <FieldVicePresidentEntry />} />
          <Route
            exact
            path={UserRouteConstants.EDIT_FIELD_VICE_PRESIDENT_USER(':userId')}
            render={() => <FieldVicePresidentEntry />}
          />
          <Route exact path={UserRouteConstants.ADD_BENEFICIARIES_USER} render={() => <AddBeneficiariesUserRoutes />} />
          <Route exact path={UserRouteConstants.ADD_BOSTOOLS_USER} render={() => <AddBOSToolsUserRoutes />} />
        </>
      )}
    />
  );
};
